import { c as _c } from "react-compiler-runtime";
import { runAfterTransition, useResizeObserver } from "@react-aria/utils";
import * as React from "react";
import { Button, ComboBox as AriaComboBox, ComboBoxStateContext, FieldErrorContext, Group, ListBox, ListBoxItem, ListLayout, Popover, Text, Virtualizer } from "react-aria-components";
import { Check, ChevronDown, XCircle } from "../icons/line.js";
import { button, buttonGroup, checkedIcon, chevronIcon, clearButton as clearButtonStyle, clearButtonIcon, comboBox, container, item, itemHeight, itemIcon, itemText, itemWrapper, listBox, popover } from "./ComboBox.css.js";
import { Input } from "./Input.js";
import { InputSubtext } from "./InputSubtext.js";
import { Label } from "./Label.js";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const ClearButton = t0 => {
  const $ = _c(14);
  const {
    clearButton
  } = t0;
  const comboboxState = React.useContext(ComboBoxStateContext);
  const errorState = React.useContext(FieldErrorContext);
  const [isFakeOpen, setIsFakeOpen] = React.useState(false);
  const wasOpen = React.useRef(false);
  let t1;
  if ($[0] !== comboboxState?.isOpen) {
    t1 = function delayedClosing() {
      if (comboboxState?.isOpen === false && wasOpen.current) {
        wasOpen.current = false;
        setTimeout(() => {
          setIsFakeOpen(false);
        }, 200);
      } else {
        if (comboboxState?.isOpen === true && !wasOpen.current) {
          wasOpen.current = true;
          requestAnimationFrame(() => {
            setIsFakeOpen(true);
          });
        }
      }
    };
    $[0] = comboboxState?.isOpen;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = comboboxState?.isOpen;
  let t3;
  if ($[2] !== t2) {
    t3 = [t2];
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  React.useEffect(t1, t3);
  if (!comboboxState || clearButton === false) {
    return null;
  }
  const isComboboxOpen = comboboxState.isOpen || isFakeOpen;
  const isInvalid = errorState?.isInvalid === true;
  const hasSelection = comboboxState.selectedKey != null;
  let visible;
  if (isInvalid) {
    visible = isFakeOpen && !isComboboxOpen ? "delayed" : isComboboxOpen;
  } else {
    visible = hasSelection;
  }
  let t4;
  if ($[4] !== comboboxState) {
    t4 = () => {
      comboboxState.setSelectedKey(null);
    };
    $[4] = comboboxState;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== visible) {
    t5 = clearButtonStyle({
      isVisible: visible
    });
    $[6] = visible;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = typeof clearButton === "string" ? clearButton : "Clear";
  const t7 = visible === false;
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = /*#__PURE__*/_jsx(XCircle, {
      className: clearButtonIcon
    });
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== t4 || $[10] !== t5 || $[11] !== t6 || $[12] !== t7) {
    t9 = /*#__PURE__*/_jsx(Button, {
      slot: null,
      onPress: t4,
      className: t5,
      "aria-label": t6,
      isDisabled: t7,
      children: t8
    });
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  return t9;
};

// We need a `id` property on the object to be able to use it in the ComboBox.
// Otherwise, the ComboBoxItem must set the `id` property when using it. This is
// a runtime error, so it's better to just require the `id` property on the
// object, and catch this compile time.

export function ComboBox(t0) {
  const $ = _c(50);
  let children;
  let description;
  let icon;
  let label;
  let placeholder;
  let props;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== t0) {
    ({
      label,
      description,
      children,
      placeholder,
      icon,
      menuTrigger: t1,
      clearButton: t2,
      virtualized: t3,
      animationDuration: t4,
      size: t5,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = description;
    $[3] = icon;
    $[4] = label;
    $[5] = placeholder;
    $[6] = props;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    children = $[1];
    description = $[2];
    icon = $[3];
    label = $[4];
    placeholder = $[5];
    props = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
  }
  const menuTrigger = t1 === undefined ? "focus" : t1;
  const clearButton = t2 === undefined ? false : t2;
  const virtualized = t3 === undefined ? false : t3;
  const animationDuration = t4 === undefined ? 100 : t4;
  const size = t5 === undefined ? "m" : t5;
  const ref = React.useRef(null);
  const [inputWidth, setInputWidth] = React.useState(0);
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      if (ref.current) {
        const width = ref.current.getBoundingClientRect().width;
        setInputWidth(width);
      }
    };
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const onResize = t6;
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      ref,
      onResize
    };
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  useResizeObserver(t7);
  let t8;
  let t9;
  if ($[14] !== animationDuration) {
    t8 = () => {
      const timeout = setTimeout(() => {
        runAfterTransition(onResize);
      }, animationDuration);
      return () => {
        clearTimeout(timeout);
      };
    };
    t9 = [animationDuration, onResize];
    $[14] = animationDuration;
    $[15] = t8;
    $[16] = t9;
  } else {
    t8 = $[15];
    t9 = $[16];
  }
  React.useEffect(t8, t9);
  let t10;
  if ($[17] !== label) {
    t10 = label != null && /*#__PURE__*/_jsx(Label, {
      children: label
    });
    $[17] = label;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== icon || $[20] !== placeholder || $[21] !== size) {
    t11 = /*#__PURE__*/_jsx(Input, {
      size: size,
      leftIcon: icon,
      placeholder: placeholder
    });
    $[19] = icon;
    $[20] = placeholder;
    $[21] = size;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== clearButton) {
    t12 = /*#__PURE__*/_jsx(ClearButton, {
      clearButton: clearButton
    });
    $[23] = clearButton;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = /*#__PURE__*/_jsx(ComboboxArrowButton, {});
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t12) {
    t14 = /*#__PURE__*/_jsxs(Group, {
      className: buttonGroup,
      children: [t12, t13]
    });
    $[26] = t12;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t11 || $[29] !== t14) {
    t15 = /*#__PURE__*/_jsxs(Group, {
      className: container,
      children: [t11, t14]
    });
    $[28] = t11;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== description) {
    t16 = /*#__PURE__*/_jsx(InputSubtext, {
      text: description
    });
    $[31] = description;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== inputWidth) {
    t17 = {
      width: inputWidth
    };
    $[33] = inputWidth;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== children) {
    t18 = /*#__PURE__*/_jsx(ListBox, {
      className: listBox,
      children: children
    });
    $[35] = children;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== t18 || $[38] !== virtualized) {
    t19 = /*#__PURE__*/_jsx(OptionalVirtualizedList, {
      virtualized: virtualized,
      children: t18
    });
    $[37] = t18;
    $[38] = virtualized;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== t17 || $[41] !== t19) {
    t20 = /*#__PURE__*/_jsx(Popover, {
      triggerRef: ref,
      style: t17,
      placement: "bottom",
      offset: 4,
      className: popover,
      children: t19
    });
    $[40] = t17;
    $[41] = t19;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== menuTrigger || $[44] !== props || $[45] !== t10 || $[46] !== t15 || $[47] !== t16 || $[48] !== t20) {
    t21 = /*#__PURE__*/_jsxs(AriaComboBox, {
      className: comboBox,
      ...props,
      menuTrigger: menuTrigger,
      ref: ref,
      children: [t10, t15, t16, t20]
    });
    $[43] = menuTrigger;
    $[44] = props;
    $[45] = t10;
    $[46] = t15;
    $[47] = t16;
    $[48] = t20;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  return t21;
}
function ComboboxArrowButton() {
  const $ = _c(4);
  const state = React.useContext(ComboBoxStateContext);
  const t0 = state?.isOpen ?? false;
  let t1;
  if ($[0] !== t0) {
    t1 = chevronIcon({
      isOpen: t0
    });
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = /*#__PURE__*/_jsx(Button, {
      className: button,
      children: /*#__PURE__*/_jsx(ChevronDown, {
        className: t1
      })
    });
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
const VirtualizedContext = /*#__PURE__*/React.createContext(false);
function OptionalVirtualizedList(t0) {
  const $ = _c(5);
  const {
    children,
    virtualized
  } = t0;
  if (!virtualized) {
    let t1;
    if ($[0] !== children) {
      t1 = /*#__PURE__*/_jsx(_Fragment, {
        children: children
      });
      $[0] = children;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      rowHeight: itemHeight
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== children) {
    t2 = /*#__PURE__*/_jsx(Virtualizer, {
      layout: ListLayout,
      layoutOptions: t1,
      children: /*#__PURE__*/_jsx(VirtualizedContext.Provider, {
        value: true,
        children: children
      })
    });
    $[3] = children;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
export function ComboBoxItem(t0) {
  const $ = _c(12);
  let Icon;
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      icon: Icon,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = Icon;
    $[2] = children;
    $[3] = props;
  } else {
    Icon = $[1];
    children = $[2];
    props = $[3];
  }
  const virtualized = React.useContext(VirtualizedContext);
  let t1;
  if ($[4] !== Icon || $[5] !== children || $[6] !== virtualized) {
    t1 = t2 => {
      const {
        isSelected,
        isFocused,
        isDisabled
      } = t2;
      return /*#__PURE__*/_jsxs("div", {
        className: item({
          isFocused,
          isSelected,
          isDisabled,
          virtualized
        }),
        children: [Icon && /*#__PURE__*/_jsx(Icon, {
          className: itemIcon
        }), /*#__PURE__*/_jsx(Text, {
          className: itemText({
            virtualized
          }),
          slot: "label",
          children: children
        }), isSelected && /*#__PURE__*/_jsx(Check, {
          className: checkedIcon
        })]
      });
    };
    $[4] = Icon;
    $[5] = children;
    $[6] = virtualized;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== children || $[9] !== props || $[10] !== t1) {
    t2 = /*#__PURE__*/_jsx(ListBoxItem, {
      textValue: children,
      ...props,
      className: itemWrapper,
      children: t1
    });
    $[8] = children;
    $[9] = props;
    $[10] = t1;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
}
//# sourceMappingURL=ComboBox.js.map